import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Wifi, 
  Activity, 
  Thermometer,
  Clock,
  Smartphone,
  ChevronRight,
  Settings,
  MapPin,
  Battery
} from 'lucide-react';

interface DeviceConfig {
  device_id: string;
  nickname?: string;
  raw_config: any;
  last_updated: string;
  is_active?: boolean;
}

interface ParameterGroup {
  title: string;
  icon: any;
  color: string;
  parameters: Array<{
    label: string;
    key: string;
    f0Key?: string;
    format?: (value: any) => string | JSX.Element;
  }>;
}

export default function DevicesPageV2() {
  const [devices, setDevices] = useState<DeviceConfig[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<DeviceConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['all']));

  useEffect(() => {
    loadDevices();
  }, []);

  const loadDevices = async () => {
    try {
      setLoading(true);
      
      const { data: configs, error: configError } = await supabase
        .from('device_config')
        .select('*')
        .order('device_id');

      if (configError) throw configError;

      const { data: settings } = await supabase
        .from('device_settings')
        .select('device_id, nickname, is_active');

      const mergedDevices = configs?.map(config => {
        const setting = settings?.find(s => s.device_id === config.device_id);
        return {
          ...config,
          nickname: setting?.nickname || config.device_id,
          is_active: setting?.is_active !== false
        };
      }) || [];

      setDevices(mergedDevices);
      
      if (mergedDevices.length > 0) {
        setSelectedDevice(mergedDevices[0]);
      }
    } catch (error) {
      console.error('Error loading devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatInterval = (seconds: any) => {
    if (!seconds && seconds !== 0) return '—';
    const num = Number(seconds);
    if (num < 60) return `${num} sec`;
    if (num < 3600) return `${Math.floor(num/60)} min`;
    if (num < 86400) return `${Math.floor(num/3600)} hours`;
    return `${Math.floor(num/86400)} days`;
  };

  const formatBoolean = (value: any) => {
    if (value === true || value === 1 || value === '1') {
      return <span className="text-green-600 font-medium">Enabled</span>;
    }
    if (value === false || value === 0 || value === '0') {
      return <span className="text-gray-400">Disabled</span>;
    }
    return <span className="text-gray-400">—</span>;
  };

  const getConfigValue = (config: any, key: string, f0Key?: string) => {
    // Try direct key first
    let value = config?.[key];
    
    // Try F0 parameters if no direct value and f0Key is provided
    if ((value === null || value === undefined) && f0Key && config?.f0_params) {
      const f0Param = config.f0_params[f0Key];
      if (f0Param) {
        value = f0Param.parsed !== undefined && f0Param.parsed !== null ? f0Param.parsed : f0Param.value;
      }
    }
    
    return value;
  };

  // Focus on parameters that actually exist in raw_config
  const parameterGroups: ParameterGroup[] = [
    {
      title: 'Network & Connectivity',
      icon: Wifi,
      color: 'text-blue-500',
      parameters: [
        { label: 'Server IP', key: 'server_ip' },
        { label: 'Server Port', key: 'server_port' },
        { label: 'NTP Server', key: 'ntp_server' },
        { label: 'NTP Port', key: 'ntp_port' },
        { label: 'Timezone', key: 'timezone', format: (v: any) => v !== undefined && v !== null ? `UTC${v >= 0 ? '+' : ''}${v}` : '—' },
        { label: 'Device ID', key: 'device_id_config' },
      ]
    },
    {
      title: 'Reporting Intervals',
      icon: Clock,
      color: 'text-green-500',
      parameters: [
        { label: 'Reporting Interval', key: 'reporting_interval', format: formatInterval },
        { label: 'Movement Report Interval', key: 'movement_report_interval', format: formatInterval },
        { label: 'Report Interval', key: 'report_interval', format: formatInterval },
        { label: 'Report Interval (Idle)', key: 'report_interval_idle', format: formatInterval },
        { label: 'Sampling Interval', key: 'sampling_interval', format: formatInterval },
      ]
    },
    {
      title: 'Motion & G-Sensor',
      icon: Activity,
      color: 'text-purple-500',
      parameters: [
        { label: 'G-Sensor Enable', key: 'gsensor_enable', format: formatBoolean },
        { label: 'G-Sensor Threshold', key: 'gsensor_threshold' },
        { label: 'G-Sensor Sensitivity', key: 'gsensor_sensitivity' },
        { label: 'G-Sensor Range', key: 'gsensor_range' },
        { label: 'G-Sensor Count', key: 'gsensor_count' },
        { label: 'G-Sensor Time Window', key: 'gsensor_time_window', format: (v: any) => v ? `${v} sec` : '—' },
        { label: 'G-Sensor Alarm Delay', key: 'gsensor_alarm_delay', format: (v: any) => v ? `${v} sec` : '—' },
      ]
    },
    {
      title: 'Environmental Monitoring',
      icon: Thermometer,
      color: 'text-orange-500',
      parameters: [
        { label: 'Sensor Alarm Enabled', key: 'sensor_alarm_enabled', format: formatBoolean },
        { label: 'Temperature Low', key: 'temp_threshold_low', format: (v: any) => v !== undefined && v !== null ? `${v}°C` : '—' },
        { label: 'Temperature High', key: 'temp_threshold_high', format: (v: any) => v !== undefined && v !== null ? `${v}°C` : '—' },
        { label: 'Humidity Low', key: 'humidity_threshold_low', format: (v: any) => v !== undefined && v !== null ? `${v}%` : '—' },
        { label: 'Humidity High', key: 'humidity_threshold_high', format: (v: any) => v !== undefined && v !== null ? `${v}%` : '—' },
        { label: 'Light Sensor Timer', key: 'light_sensor_timer', format: formatInterval },
        { label: 'Sensor Alarm Timer', key: 'sensor_alarm_timer', format: formatInterval },
      ]
    },
    {
      title: 'GPS & Location',
      icon: MapPin,
      color: 'text-red-500',
      parameters: [
        { label: 'Geofence Enable', key: 'geofence_enable', format: formatBoolean },
        { label: 'GPS Blindness Timer', key: 'gps_blindness_timer', format: formatInterval },
        { label: 'Max Speed Threshold', key: 'max_speed_threshold', format: (v: any) => v !== undefined && v !== null ? `${v} km/h` : '—' },
      ]
    },
    {
      title: 'Battery & Power',
      icon: Battery,
      color: 'text-yellow-500',
      parameters: [
        { label: 'Low Battery Threshold', key: 'low_battery_threshold', format: (v: any) => v !== undefined && v !== null ? `${v}%` : '—' },
      ]
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">Device Configuration</h1>
        
        <div className="grid grid-cols-12 gap-6">
          {/* Device List */}
          <div className="col-span-3">
            <Card>
              <div className="p-4 border-b">
                <h2 className="font-semibold">Devices</h2>
              </div>
              <div className="divide-y">
                {devices.map(device => (
                  <div
                    key={device.device_id}
                    className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedDevice?.device_id === device.device_id ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => setSelectedDevice(device)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-sm">{device.nickname || device.device_id}</div>
                        <div className="text-xs text-gray-500">{device.device_id}</div>
                      </div>
                      <Badge variant={device.is_active ? 'default' : 'secondary'}>
                        {device.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Configuration Display */}
          <div className="col-span-9">
            {selectedDevice ? (
              <div className="space-y-4">
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-semibold">{selectedDevice.nickname || selectedDevice.device_id}</h2>
                      <p className="text-sm text-gray-500">Last updated: {new Date(selectedDevice.last_updated).toLocaleString()}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setExpandedGroups(new Set(['all']))}
                        className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        Expand All
                      </button>
                      <button
                        onClick={() => setExpandedGroups(new Set())}
                        className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                      >
                        Collapse All
                      </button>
                    </div>
                  </div>
                </Card>

                {parameterGroups.map((group, groupIndex) => {
                  const Icon = group.icon;
                  const isExpanded = expandedGroups.has('all') || expandedGroups.has(group.title);
                  
                  return (
                    <Card key={groupIndex}>
                      <div 
                        className="p-3 cursor-pointer hover:bg-gray-50"
                        onClick={() => {
                          const newExpanded = new Set(expandedGroups);
                          if (newExpanded.has(group.title)) {
                            newExpanded.delete(group.title);
                          } else {
                            newExpanded.add(group.title);
                          }
                          setExpandedGroups(newExpanded);
                        }}
                      >
                        <div className="text-sm flex items-center justify-between">
                          <div className="flex items-center">
                            <Icon className={`w-4 h-4 mr-2 ${group.color}`} />
                            <span className="font-semibold">{group.title}</span>
                          </div>
                          <ChevronRight className={`w-4 h-4 text-gray-400 transform transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="px-3 pb-3">
                          <div className="space-y-1">
                            {group.parameters.map((param, idx) => {
                              const value = getConfigValue(selectedDevice.raw_config, param.key, param.f0Key);
                              const displayValue = param.format ? param.format(value) : (value ?? '—');
                              
                              return (
                                <div key={idx} className="flex justify-between items-center py-1 text-xs">
                                  <span className="text-gray-600">{param.label}</span>
                                  <span className="font-medium text-gray-900">{displayValue}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </Card>
                  );
                })}

                {/* F0 Parameters */}
                {selectedDevice.raw_config?.f0_params && (
                  <Card>
                    <div 
                      className="p-3 cursor-pointer hover:bg-gray-50"
                      onClick={() => {
                        const newExpanded = new Set(expandedGroups);
                        if (newExpanded.has('f0_params')) {
                          newExpanded.delete('f0_params');
                        } else {
                          newExpanded.add('f0_params');
                        }
                        setExpandedGroups(newExpanded);
                      }}
                    >
                      <div className="text-sm flex items-center justify-between">
                        <div className="flex items-center">
                          <Settings className="w-4 h-4 mr-2 text-gray-500" />
                          <span className="font-semibold">All F0 Parameters</span>
                        </div>
                        <ChevronRight className={`w-4 h-4 text-gray-400 transform transition-transform ${expandedGroups.has('f0_params') ? 'rotate-90' : ''}`} />
                      </div>
                    </div>
                    {expandedGroups.has('f0_params') && (
                      <div className="px-3 pb-3">
                        <div className="space-y-1">
                          {Object.entries(selectedDevice.raw_config.f0_params).map(([key, value]: [string, any]) => {
                            const displayValue = value.parsed !== undefined && value.parsed !== null 
                              ? `${value.parsed}` 
                              : value.value || '—';
                            
                            return (
                              <div key={key} className="flex justify-between items-center py-1 text-xs">
                                <span className="text-gray-600 font-mono">{key}</span>
                                <span className="font-medium text-gray-900">
                                  {displayValue}
                                  {value.parsed !== undefined && value.parsed !== null && value.value && value.value !== displayValue && (
                                    <span className="text-gray-400 ml-1">({value.value})</span>
                                  )}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </Card>
                )}
              </div>
            ) : (
              <Card className="h-full min-h-[400px] flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <Smartphone className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Select a device to view its configuration</p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}